/**
 * LA forma de decir una fecha en todo el front: `dd/mm/aaaa`, sin hora
 * (`formatDateTime` le suma la hora donde importa el orden de llegada).
 *
 * Una sola función para que todas se vean igual --la cola, el campo Fecha,
 * las listas y los detalles, el Resumen, el Acierto, el Consumo--. Lo que
 * no es una fecha se queda como está: una duración («5 min»), lo relativo
 * («hace 5 min»), un mes («octubre de 2026») o una hora suelta.
 */

const pad = (value: number): string => String(value).padStart(2, "0");

/** Un día del calendario, `AAAA-MM-DD`, sin hora: no cambia con la zona. */
const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `2026-09-29T14:43:00Z` o `2026-09-29` → `29/09/2026`.
 *
 * Un instante se dice en la hora de quien mira (el equipo está en Quito y su
 * navegador también); un día del calendario, tal cual. Sin fecha, o una que
 * no se entiende, «—».
 */
export const formatDate = (value: string | Date | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") {
    const day = CALENDAR_DAY.exec(value);
    if (day !== null) return `${day[3] ?? ""}/${day[2] ?? ""}/${day[1] ?? ""}`;
  }
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "—";
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${String(date.getFullYear())}`;
};

/**
 * `2026-09-29T14:43:00Z` → `29/09/2026 09:43`: la fecha de siempre más la
 * hora (24 h, en la hora de quien mira). Para los listados donde importa el
 * orden de llegada dentro del mismo día. Un día del calendario no tiene hora
 * y se dice como `formatDate`.
 *
 * `{ year: false }` → `29/09 09:43`, para donde no cabe (la fila de la cola,
 * que es del mes en curso): el `title` lleva la fecha completa.
 */
export const formatDateTime = (
  value: string | Date | null | undefined,
  { year = true }: { readonly year?: boolean } = {},
): string => {
  if (typeof value === "string" && CALENDAR_DAY.test(value)) {
    return formatDate(value);
  }
  const day = formatDate(value);
  if (day === "—" || value === null || value === undefined) return day;
  const date = typeof value === "string" ? new Date(value) : value;
  const shown = year ? day : day.slice(0, 5);
  return `${shown} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
